/**
 * Markdown-LITE renderer for assistant messages — covers exactly the subset
 * the backend system prompt licenses ("plain text or light Markdown"):
 * **bold**, *italic*, `inline code`, "- "/"* " bullets, "1. " numbered
 * lists, #/##/### headings (rendered as bold lines), "> " blockquotes,
 * ```fenced code blocks``` and simple pipe tables.
 *
 * Hand-rolled on purpose:
 * - Builds React NODES, never innerHTML — model output can echo
 *   tenant-authored strings, so script injection must be impossible by
 *   construction, not by sanitizer config.
 * - Links: ONLY internal app paths (a single leading "/", no protocol) render
 *   as clickable SPA links — e.g. the company assistant's deep-link to an
 *   event. Any external/absolute URL stays selectable plain text, so a
 *   prompt-injected `http(s)`/`javascript:` URL can never become a
 *   trusted-looking off-site anchor.
 * - Zero new dependencies (no markdown lib exists in the repo).
 *
 * Streaming note: an unterminated marker (e.g. "**bol" mid-stream) renders
 * literally and snaps to bold when the closing marker arrives; an unterminated
 * code fence still renders as a code block (graceful tail). Pass {caret:true}
 * while streaming to append a blinking caret to the last paragraph.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { CodeBlock } from "./chat/CodeBlock";

const INLINE_TOKEN =
  /(\[[^\]\n]+\]\([^)\s\n]+\)|`[^`\n]+`|\*\*[^*\n]+?\*\*|\*[^*\n]+?\*|_[^_\n]+?_)/g;

/** A safe internal SPA path: one leading "/", NOT "//" (protocol-relative),
 *  and only ordinary path characters — so no `http(s)://`, `javascript:` etc.
 *  can ever pass. Query/hash are allowed for flexibility. */
const SAFE_INTERNAL_PATH = /^\/(?!\/)[A-Za-z0-9\-._~/?#=&%]*$/;

const LINK_TOKEN = /^\[([^\]]+)\]\(([^)]+)\)$/;

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const match of text.matchAll(INLINE_TOKEN)) {
    const idx = match.index ?? 0;
    if (idx > last) nodes.push(text.slice(last, idx));
    const token = match[0];
    const key = `${keyPrefix}-${i++}`;
    if (token.startsWith("[")) {
      const m = LINK_TOKEN.exec(token);
      if (m && SAFE_INTERNAL_PATH.test(m[2])) {
        nodes.push(
          <Link
            key={key}
            to={m[2]}
            className="underline underline-offset-2 font-medium hover:opacity-80"
          >
            {m[1]}
          </Link>,
        );
      } else {
        // External / unsafe URL: keep the literal text (URL stays visible but
        // is NOT a clickable anchor — the no-phishing guarantee).
        nodes.push(token);
      }
    } else if (token.startsWith("`")) {
      nodes.push(
        <code key={key} className="surface-tertiary rounded px-1 py-0.5 text-[0.85em] font-mono">
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith("**")) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else {
      // *italic* or _italic_
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    }
    last = idx + token.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

// List markers — each captures (1) the leading indent and the item text, so
// nesting depth is derived from indentation instead of being discarded.
const BULLET_LINE = /^(\s*)[-*]\s+(.*)$/;
const NUMBERED_LINE = /^(\s*)(\d+)[.)]\s+(.*)$/;
// Alpha sub-points ("a. ", "b) ", "A. "). Only treated as a list item when
// indented OR continuing an open list (see guard below), so a top-level prose
// line like "A. Lincoln freed the slaves" never becomes a bogus list.
const ALPHA_LINE = /^(\s*)([A-Za-z])[.)]\s+(.*)$/;
const HEADING_LINE = /^#{1,4}\s+(.*)$/;
const QUOTE_LINE = /^\s*>\s?(.*)$/;
const FENCE_LINE = /^\s*```(.*)$/;
// A table row has at least one interior pipe; the header is only treated as a
// table when the NEXT line is a separator (---|---), so prose with a stray "|"
// never false-triggers.
const TABLE_ROW = /\|/;
const TABLE_SEP = /^\s*\|?(\s*:?-+:?\s*\|)+\s*:?-*:?\s*$/;

/** "| a | b |" → ["a", "b"] (tolerates missing outer pipes). */
function splitTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());
}

export function renderMarkdownLite(
  content: string,
  opts?: { caret?: boolean },
): ReactNode[] {
  const lines = content.split("\n");
  const blocks: ReactNode[] = [];
  let listItems: { depth: number; marker: string; text: string }[] = [];
  // Open indent widths, one per nesting level. Distinct increasing widths map to
  // deeper levels (tolerant of 2/3/4-space indents); reset on each flushList.
  let indentStack: number[] = [];
  let quoteLines: string[] = [];
  let key = 0;
  // Index + inline nodes of the last paragraph block, so a streaming caret can
  // be appended INLINE (no extra line / layout shift) when content ends in prose.
  let lastParagraph: { idx: number; nodes: ReactNode[] } | null = null;

  // Map a leading-indent width to a nesting depth (0-based) using indentStack.
  const depthFor = (width: number): number => {
    while (indentStack.length && indentStack[indentStack.length - 1] > width) {
      indentStack.pop();
    }
    if (!indentStack.length || indentStack[indentStack.length - 1] < width) {
      indentStack.push(width);
    }
    return indentStack.length - 1;
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    const items = listItems;
    const listKey = key++;
    // Render the slice of items at `depth` as one <ul>; whenever the next item
    // is deeper, recurse and nest the child <ul> inside the current li's
    // content span (so it indents under the parent marker). Returns the <ul>
    // node and the index of the first item NOT consumed at this depth.
    const renderLevel = (start: number, depth: number): { node: ReactNode; next: number } => {
      const children: ReactNode[] = [];
      let i = start;
      while (i < items.length && items[i].depth === depth) {
        const item = items[i];
        const idx = i;
        i++;
        let nested: ReactNode = null;
        if (i < items.length && items[i].depth > depth) {
          const sub = renderLevel(i, items[i].depth);
          nested = sub.node;
          i = sub.next;
        }
        children.push(
          <li key={idx} className="flex gap-1.5">
            <span className="shrink-0 select-none">{item.marker}</span>
            <span className="min-w-0">
              {renderInline(item.text, `li-${listKey}-${idx}`)}
              {nested}
            </span>
          </li>,
        );
      }
      return {
        node: (
          <ul
            key={`l-${listKey}-${depth}-${start}`}
            className={depth === 0 ? "my-0.5 flex flex-col gap-0.5" : "mt-0.5 flex flex-col gap-0.5"}
          >
            {children}
          </ul>
        ),
        next: i,
      };
    };
    blocks.push(renderLevel(0, 0).node);
    listItems = [];
    indentStack = [];
    lastParagraph = null;
  };

  const flushQuote = () => {
    if (quoteLines.length === 0) return;
    blocks.push(
      <blockquote
        key={`q-${key++}`}
        className="my-1 border-l-2 outline-quaternary pl-2.5 font-tertiary"
      >
        {quoteLines.map((q, i) => (
          <div key={i}>{renderInline(q, `q-${key}-${i}`)}</div>
        ))}
      </blockquote>,
    );
    quoteLines = [];
    lastParagraph = null;
  };

  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];

    // Fenced code block — accumulate raw lines until the closing fence (or EOF).
    const fence = FENCE_LINE.exec(line);
    if (fence) {
      flushList();
      flushQuote();
      const lang = fence[1]?.trim() || undefined;
      const codeLines: string[] = [];
      li++;
      while (li < lines.length && !FENCE_LINE.test(lines[li])) {
        codeLines.push(lines[li]);
        li++;
      }
      // li now points at the closing fence (consumed) or past EOF (unterminated).
      blocks.push(<CodeBlock key={`c-${key++}`} code={codeLines.join("\n")} lang={lang} />);
      continue;
    }

    const bullet = BULLET_LINE.exec(line);
    if (bullet) {
      flushQuote();
      listItems.push({ depth: depthFor(bullet[1].length), marker: "•", text: bullet[2] });
      continue;
    }
    const numbered = NUMBERED_LINE.exec(line);
    if (numbered) {
      flushQuote();
      listItems.push({
        depth: depthFor(numbered[1].length),
        marker: `${numbered[2]}.`,
        text: numbered[3],
      });
      continue;
    }
    // Alpha sub-points: only a list item when indented or continuing an open
    // list — otherwise it's prose (e.g. "A. Lincoln freed the slaves").
    const alpha = ALPHA_LINE.exec(line);
    if (alpha && (alpha[1].length > 0 || listItems.length > 0)) {
      flushQuote();
      listItems.push({
        depth: depthFor(alpha[1].length),
        marker: `${alpha[2]}.`,
        text: alpha[3],
      });
      continue;
    }
    flushList();

    // Blockquote — accumulate consecutive "> " lines.
    const quote = QUOTE_LINE.exec(line);
    if (quote) {
      quoteLines.push(quote[1]);
      continue;
    }
    flushQuote();

    // Pipe table — only when the next line is a separator row.
    if (
      TABLE_ROW.test(line) &&
      li + 1 < lines.length &&
      TABLE_SEP.test(lines[li + 1])
    ) {
      const header = splitTableRow(line);
      const rows: string[][] = [];
      li += 2; // skip header + separator
      while (li < lines.length && TABLE_ROW.test(lines[li]) && lines[li].trim() !== "") {
        rows.push(splitTableRow(lines[li]));
        li++;
      }
      li--; // the for-loop's li++ lands on the first non-row line
      const tKey = key++;
      blocks.push(
        <div key={`t-${tKey}`} className="my-1 overflow-x-auto">
          <table className="text-xs border-collapse">
            <thead>
              <tr>
                {header.map((cell, c) => (
                  <th
                    key={c}
                    className="border outline-quaternary px-2 py-1 text-left font-medium align-top"
                  >
                    {renderInline(cell, `th-${tKey}-${c}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>
                  {header.map((_, c) => (
                    <td
                      key={c}
                      className="border outline-quaternary px-2 py-1 align-top"
                    >
                      {renderInline(row[c] ?? "", `td-${tKey}-${r}-${c}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    const heading = HEADING_LINE.exec(line);
    if (heading) {
      blocks.push(
        <div key={`h-${key++}`} className="font-semibold mt-1">
          {renderInline(heading[1], `h-${key}`)}
        </div>,
      );
      lastParagraph = null;
      continue;
    }
    if (line.trim() === "") {
      // Collapse runs of blank lines into a single small spacer.
      const prev = blocks[blocks.length - 1];
      if (prev && typeof prev === "object" && "key" in prev && String(prev.key).startsWith("s-")) continue;
      blocks.push(<div key={`s-${key++}`} className="h-1.5" aria-hidden />);
      lastParagraph = null;
      continue;
    }
    const nodes = renderInline(line, `p-${key}`);
    lastParagraph = { idx: blocks.length, nodes };
    blocks.push(<div key={`p-${key++}`}>{nodes}</div>);
  }
  flushList();
  flushQuote();

  // Streaming caret: inline into the last paragraph when content ends in prose
  // (no extra line); otherwise a quiet standalone caret line.
  if (opts?.caret) {
    const caret = (
      <span
        key="caret"
        aria-hidden
        className="ml-0.5 inline-block h-[1em] w-[2px] -mb-[1px] bg-current animate-pulse motion-reduce:animate-none align-text-bottom"
      />
    );
    if (lastParagraph && lastParagraph.idx === blocks.length - 1) {
      blocks[lastParagraph.idx] = (
        <div key="p-caret">
          {lastParagraph.nodes}
          {caret}
        </div>
      );
    } else {
      blocks.push(
        <div key="caret-line" aria-hidden>
          {caret}
        </div>,
      );
    }
  }

  return blocks;
}
